/**
 * The session, as one query and one side effect.
 *
 * The query is `GET /auth/me`; the side effect is the global 401 handler. Nothing is kept in
 * `localStorage` — a user record there is a copy of a server-side fact that can be edited by
 * anyone with a console, and the only authority on who you are is the session cookie the browser
 * will not let this code read.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { AuthUserDto } from '@catalog/shared';

import { authConfigQuery, login, logout, meQuery } from '../api/auth.js';
import { setUnauthorizedHandler } from '../api/client.js';
import { queryKeys } from '../api/query-keys.js';
import { AuthContext, type AuthContextValue } from './auth-context.js';

export interface AuthProviderProps {
  children?: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const me = useQuery(meQuery());
  const config = useQuery(authConfigQuery());

  /**
   * One handler for every 401 in the app.
   *
   * Registered in an effect so it is torn down with the provider, and it reads the *current*
   * location through a ref-like closure over `location.pathname` — which is why `location` is in
   * the dependency list. Re-registering on navigation is cheap and means the `returnTo` always
   * points at the page the user was actually on.
   *
   * `GET /auth/me` is exempt at the fetch layer, so an anonymous visitor browsing the public
   * catalog never reaches this.
   */
  useEffect(() => {
    return setUnauthorizedHandler(() => {
      queryClient.setQueryData(queryKeys.me(), null);

      const returnTo = `${location.pathname}${location.search}`;
      const alreadyThere = location.pathname === '/login';

      if (!alreadyThere) {
        void navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
      }
    });
  }, [queryClient, navigate, location.pathname, location.search]);

  /**
   * `setQueryData` rather than `invalidateQueries`.
   *
   * The sign-in response *is* the user, so refetching `/auth/me` to learn what we were just told
   * adds a round trip during which the app still believes nobody is signed in — long enough for
   * a `ProtectedRoute` to bounce the user straight back to the login page they just left.
   */
  const adoptSession = useCallback(
    (user: AuthUserDto) => {
      queryClient.setQueryData(queryKeys.me(), user);
    },
    [queryClient],
  );

  const signInMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) =>
      login({ email: credentials.email, password: credentials.password }),
    onSuccess: adoptSession,
  });

  const signOutMutation = useMutation({
    mutationFn: logout,
    /**
     * `onSettled`, not `onSuccess`: if the call failed because the session had already expired,
     * the user is signed out whether or not the request succeeded, and leaving the cached user in
     * place would show them a signed-in header over a dead session.
     *
     * The whole cache is cleared, not just the session — every page of lessons and transcripts
     * already fetched is licensed content belonging to the session that just ended.
     */
    onSettled: () => {
      queryClient.setQueryData(queryKeys.me(), null);
      queryClient.clear();
    },
  });

  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      await signInMutation.mutateAsync({ email, password });
    },
    [signInMutation],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await signOutMutation.mutateAsync();
  }, [signOutMutation]);

  const user = me.data ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isPending: me.isPending,
      error: me.error,
      isAuthenticated: user !== null,
      isAdmin: user?.role === 'admin',
      // Until `/auth/config` answers, assume SSO is off: a button that 503s is worse than one
      // that appears a moment late.
      ssoEnabled: config.data?.ssoEnabled ?? false,
      ssoLoginPath: config.data?.ssoLoginPath ?? '/auth/ex/login',
      signIn,
      signOut,
      adoptSession,
    }),
    [user, me.isPending, me.error, config.data, signIn, signOut, adoptSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
